import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, CircularProgress,
} from '@mui/material';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';

const LEAVE_STATUS_COLOR = { pending: 'warning', approved: 'success', rejected: 'error' };
const getLeaveStatus = (lr) => (lr.approved ? 'approved' : lr.rejected ? 'rejected' : 'pending');

export default function LeaveSection() {
  const { getToken } = useAuth();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    type: 'annual', dateStart: '', dateEnd: '', hours: 8, notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [myLeave, setMyLeave] = useState([]);

  const fetchLeave = useCallback(async () => {
    try {
      const t = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/leave/my`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await resp.json();
      if (data.success) setMyLeave(data.leave.slice(0, 5));
    } catch (e) {
      console.warn('Could not fetch leave', e);
    }
  }, [getToken]);

  useEffect(() => { fetchLeave(); }, [fetchLeave]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const t = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      if (data.success) {
        setDialogOpen(false);
        setForm({ type: 'annual', dateStart: '', dateEnd: '', hours: 8, notes: '' });
        fetchLeave();
      } else {
        alert(data.message || 'Failed to submit leave request');
      }
    } catch {
      alert('Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Paper sx={{ mt: 3, p: 2.5, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: myLeave.length > 0 ? 2 : 0 }}>
          <Typography variant="h6" fontWeight={600}>
            <BeachAccessIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            My Leave
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BeachAccessIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Request Time Off
          </Button>
        </Box>

        {myLeave.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myLeave.map(lr => {
                const status = getLeaveStatus(lr);
                return (
                  <TableRow key={lr.id}>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {lr.type?.replace('_', ' ')}
                    </TableCell>
                    <TableCell>
                      {new Date(lr.date_start).toLocaleDateString('en-AU')}
                      {lr.date_start !== lr.date_end &&
                        ` – ${new Date(lr.date_end).toLocaleDateString('en-AU')}`}
                    </TableCell>
                    <TableCell align="right">{lr.hours}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={status}
                        color={LEAVE_STATUS_COLOR[status]}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No leave requests on record.
          </Typography>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Time Off</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Leave Type</InputLabel>
            <Select
              value={form.type}
              label="Leave Type"
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            >
              <MenuItem value="annual">Annual Leave</MenuItem>
              <MenuItem value="sick">Sick Day</MenuItem>
              <MenuItem value="public_holiday">Public Holiday</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth type="date" label="Start Date"
                value={form.dateStart}
                onChange={e => setForm(f => ({ ...f, dateStart: e.target.value, dateEnd: f.dateEnd || e.target.value }))}
                InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="date" label="End Date"
                value={form.dateEnd}
                onChange={e => setForm(f => ({ ...f, dateEnd: e.target.value }))}
                InputLabelProps={{ shrink: true }} required
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth margin="normal" type="number" label="Hours"
            value={form.hours}
            onChange={e => setForm(f => ({ ...f, hours: parseFloat(e.target.value) || 8 }))}
            inputProps={{ min: 0.5, max: 80, step: 0.5 }}
            helperText="Total hours for this leave period (default 8 = full day)"
          />
          <TextField
            fullWidth margin="normal" label="Notes (optional)" multiline rows={2}
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            helperText={form.type === 'sick' ? 'Required for sick leave longer than 1 day' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !form.dateStart || !form.dateEnd}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
